import { useState } from "react";
import { useForm } from "@inertiajs/react";
import Button from "@/components/buttons/Button";
import Input from "@/components/inputs/Input";
import Select, { SelectOption } from "@/components/inputs/Select";
import SearchSelectSingle from "@/components/inputs/SearchSelectSingle";
import InertiaLink from "@/components/buttons/InertiaLink";
import PageHeader from "@/components/PageHeader";
import FormActions from "@/components/buttons/FormActions";

interface NewProps {
  pro_options: SelectOption[];
  nro_options: SelectOption[];
}

const New: React.FC<NewProps> = ({ pro_options, nro_options }) => {
  const { data, setData, post, processing, errors } = useForm({
    first_name: "",
    middle_name: "",
    last_name: "",
    ipi_cae_number: "",
    pro_affiliation: "",
    nro_affiliation: "",
    nro_number: "",
    user_id: "",
  });

  const [linkedUser, setLinkedUser] = useState<SelectOption | null>(null);

  const handleLinkedUserChange = (item: SelectOption | null) => {
    setLinkedUser(item);
    setData("user_id", item?.value ?? "");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post("/contributors");
  };

  return (
    <div className="flex w-full flex-col items-start justify-start gap-10">
      <PageHeader
        backLink={{ href: "/contributors", text: "Back to all contributors" }}
        title="Create new contributor"
      />
      <form
        onSubmit={handleSubmit}
        className="dark:bg-cream-950 flex w-full flex-col gap-6 rounded-2xl bg-white p-6 shadow md:p-10"
      >
        <Input
          type="text"
          label="First Name *"
          value={data.first_name}
          onChange={(e) => setData("first_name", e.target.value)}
          placeholder="Enter first name"
          errors={errors.first_name}
        />

        <Input
          type="text"
          label="Middle Name"
          value={data.middle_name}
          onChange={(e) => setData("middle_name", e.target.value)}
          placeholder="Enter middle name"
          errors={errors.middle_name}
        />

        <Input
          type="text"
          label="Last Name *"
          value={data.last_name}
          onChange={(e) => setData("last_name", e.target.value)}
          placeholder="Enter last name"
          errors={errors.last_name}
        />

        <Select
          label="PRO Affiliation"
          name="pro_affiliation"
          placeholder="Select PRO affiliation"
          options={pro_options}
          value={data.pro_affiliation}
          onChange={(e) => setData("pro_affiliation", e.target.value)}
          errors={errors.pro_affiliation}
        />

        <Input
          type="text"
          label="IPI/CAE Number"
          value={data.ipi_cae_number}
          onChange={(e) => setData("ipi_cae_number", e.target.value)}
          placeholder="Enter 9-11 digit IPI/CAE number"
          errors={errors.ipi_cae_number}
        />

        <Select
          label="NRO Affiliation"
          name="nro_affiliation"
          placeholder="Select NRO affiliation"
          options={nro_options}
          value={data.nro_affiliation}
          onChange={(e) => setData("nro_affiliation", e.target.value)}
          errors={errors.nro_affiliation}
        />

        <Input
          type="text"
          label="NRO Number"
          value={data.nro_number}
          onChange={(e) => setData("nro_number", e.target.value)}
          placeholder="Enter NRO number"
          errors={errors.nro_number}
        />

        <SearchSelectSingle
          label="Linked User"
          placeholder="Search for a user"
          reloadPropName="user_search_results"
          queryParam="user_q"
          value={linkedUser}
          onChange={handleLinkedUserChange}
          errors={errors.user_id}
          singleSelectionMessage="Only a single user can be linked to a contributor. Remove the current user to choose another."
        />

        <FormActions>
          <InertiaLink href="/contributors" type="secondary" text="Cancel" />
          <Button
            text={processing ? "Creating..." : "Create Contributor"}
            type="primary"
            htmlButtonType="submit"
            disabled={processing}
          />
        </FormActions>
      </form>
    </div>
  );
};

export default New;
